import { useEffect, useLayoutEffect, useRef, useState, type ReactNode } from "react";
import { useQuery } from "@tanstack/react-query";
import type { IconType } from "react-icons";
import { FiAlertOctagon, FiAlertTriangle, FiBell, FiCheckCircle, FiClock, FiHelpCircle, FiVolume2, FiVolumeX, FiXOctagon } from "react-icons/fi";
import { armAudio, audioArmed, playAlarm } from "@/lib/alarm";

import { getOverview } from "@/lib/api";
import { useSignalBands } from "@/lib/bands";
import { formatBps } from "@/lib/format";
import { incidentTarget, incidentTitle } from "@/lib/incident";
import { fmtNum, useI18n, type Key } from "@/lib/i18n";
import { classifyRx, fmtDbm, signalColor } from "@/lib/signal";
import { cn } from "@/lib/utils";
import MonitoringBanner from "@/components/MonitoringBanner";
import {
  ALARM_REPEAT_MS, alarmable, alarmLevel,
  alertBreakdown, dur, freshness, HOT_C, HOT_CRIT_C, incidents, netState, oltTier, ponTier, rankOLTs, rankPONs, sites, warningAlerts,
  type Freshness, type Incident, type NetState, type Site, type Tier,
} from "@/lib/wallboard";
import type { Overview } from "@/lib/types";

/**
 * The NOC wallboard: one full-screen page for the TV. Passive: no click, no
 * hover, no scroll -- every list fits what the screen holds and summarises the
 * rest, so it reads the same at 2 OLTs and at 50. Refreshes every 20s.
 *
 * NORMAL = QUIET, WARNING = NOTICEABLE, CRITICAL = IMPOSSIBLE TO MISS.
 */
const REFRESH_MS = 20_000;

/** Wall clock in the OLT's zone: the TV may sit on a machine set to anything. */
const TZ = "Asia/Jakarta";
const clockFmt = new Intl.DateTimeFormat("id-ID", { hour: "2-digit", minute: "2-digit", timeZone: TZ });

function useNow() {
  const [now, setNow] = useState(() => Date.now());
  useEffect(() => {
    const id = setInterval(() => setNow(Date.now()), 1000);
    return () => clearInterval(id);
  }, []);
  return now;
}

/**
 * Type scales with the screen, but slower than the screen: 1080p gets ~17px
 * rem, 4K ~28px, so a bigger TV shows MORE rows at a readable size rather than
 * the same rows blown up. Scoped to this route; restored on leave.
 */
function useWallType() {
  useEffect(() => {
    const html = document.documentElement;
    const before = html.style.fontSize;
    html.style.fontSize = "clamp(15px, calc(0.55vw + 7px), 30px)";
    return () => { html.style.fontSize = before; };
  }, []);
}

// --- visual vocabulary: colour never alone -- marker shape, icon and word too.

const TONE: Record<Tier | "unknown", { text: string; bg: string; icon: IconType }> = {
  down: { text: "text-signal-crit", bg: "bg-signal-crit", icon: FiXOctagon },
  degraded: { text: "text-signal-warn", bg: "bg-signal-warn", icon: FiAlertTriangle },
  warn: { text: "text-signal-warn", bg: "bg-signal-warn", icon: FiAlertTriangle },
  healthy: { text: "text-signal-good", bg: "bg-signal-good", icon: FiCheckCircle },
  unknown: { text: "text-slate-400", bg: "bg-slate-500", icon: FiHelpCircle },
};

/** Status marker: a filled square for down, a ring for degraded/warn, a small dot for healthy. */
function Marker({ tier, className }: { tier: Tier | "unknown"; className?: string }) {
  const ring = tier === "degraded" || tier === "warn";
  return (
    <span
      aria-hidden
      className={cn(
        "inline-block shrink-0",
        tier === "down" ? "rounded-[0.15em]" : "rounded-full",
        tier === "healthy" ? "size-[0.5em]" : "size-[0.72em]",
        ring ? `border-[0.18em] border-current ${TONE[tier].text}` : TONE[tier].bg,
        className,
      )}
    />
  );
}

/**
 * A list that shows as many rows as its box holds and hands the rest to
 * `rest` -- the wallboard never scrolls and never grows with the network.
 * Measures after layout (all rows, then the ones that fit), again on resize
 * and whenever the rows change.
 */
function FitList<T>({
  items, keyOf, row, rest, empty,
}: {
  items: T[]; keyOf: (t: T) => string; row: (t: T) => ReactNode; rest: (hidden: T[]) => ReactNode; empty?: ReactNode;
}) {
  const ref = useRef<HTMLDivElement>(null);
  const sig = items.map(keyOf).join("|");
  const [h, setH] = useState(0);
  const [fit, setFit] = useState<{ sig: string; h: number; n: number } | null>(null);
  const measuring = !fit || fit.sig !== sig || fit.h !== h;

  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    const ro = new ResizeObserver(() => setH(el.clientHeight));
    ro.observe(el);
    return () => ro.disconnect();
  }, []);

  useLayoutEffect(() => {
    const el = ref.current;
    if (!measuring || !el) return;
    const kids = [...el.children] as HTMLElement[];
    const top = el.getBoundingClientRect().top;
    const bottom = (i: number) => kids[i].getBoundingClientRect().bottom - top;
    let n = items.length === 0 ? 0 : kids.length;
    if (n && bottom(n - 1) > el.clientHeight) {
      // Leave one summary line's worth of room.
      const reserve = parseFloat(getComputedStyle(document.documentElement).fontSize) * 2.4;
      n = 0;
      while (n < kids.length && bottom(n) <= el.clientHeight - reserve) n++;
    }
    // Record the observed height, the value `measuring` compares against:
    // storing clientHeight here instead would never match and loop forever.
    setFit({ sig, h, n });
  });

  const n = measuring ? items.length : fit.n;
  return (
    <div ref={ref} className="flex min-h-0 flex-1 flex-col overflow-hidden">
      {items.length === 0 ? empty : items.slice(0, n).map((it) => <div key={keyOf(it)} className="shrink-0">{row(it)}</div>)}
      {!measuring && n < items.length && <div className="shrink-0">{rest(items.slice(n))}</div>}
    </div>
  );
}

function Panel({ title, aside, children }: { title: string; aside?: ReactNode; children: ReactNode }) {
  return (
    <section className="flex min-h-0 min-w-0 flex-col border border-slate-800/90 bg-slate-900/35">
      <h2 className="flex shrink-0 items-baseline justify-between gap-3 border-b border-slate-800/90 px-[1rem] py-[0.45rem] text-[0.8rem] uppercase tracking-[0.22em] text-slate-400">
        <span>{title}</span>
        {aside !== undefined && <span className="font-mono normal-case tracking-normal text-slate-400">{aside}</span>}
      </h2>
      <div className="flex min-h-0 flex-1 flex-col">{children}</div>
    </section>
  );
}

/** The "and the rest" line under a fitted list: calm when every hidden row is healthy. */
function Rest({ healthy, label }: { healthy: boolean; label: string }) {
  return (
    <div className="flex items-center gap-[0.6rem] border-t border-slate-800/70 px-[1rem] py-[0.5rem] text-[0.95rem] text-slate-400">
      <Marker tier={healthy ? "healthy" : "degraded"} />
      <span className="font-mono">{label}</span>
    </div>
  );
}

function Calm({ text }: { text: string }) {
  return (
    <div className="flex items-center gap-[0.7rem] px-[1rem] py-[0.9rem] text-[1.3rem] text-signal-good">
      <FiCheckCircle className="shrink-0" /> {text}
    </div>
  );
}

// ---------------------------------------------------------------------------

export default function WallboardPage() {
  useWallType();
  const { t } = useI18n();
  const now = useNow();
  const { data, error, isLoading, dataUpdatedAt, isError } = useQuery({
    queryKey: ["overview"],
    queryFn: getOverview,
    refetchInterval: REFRESH_MS,
    refetchIntervalInBackground: true,
  });

  // Hooks before the loading return: the alarm must see every poll, including the first (its baseline).
  const alarm = useAlarm(data ? incidents(data) : null, data ? freshness(data, now, dataUpdatedAt, isError).stale : true, dataUpdatedAt);

  if (isLoading || !data) {
    return (
      <div className="flex h-dvh items-center justify-center bg-slate-950 text-[1.4rem] text-slate-400">
        {error ? `${t("wb.apiDown")} — ${(error as Error).message}` : t("app.loading")}
      </div>
    );
  }

  const inc = incidents(data);
  const state = netState(data, inc);
  const fresh = freshness(data, now, dataUpdatedAt, isError);
  const major = inc.filter((i) => i.kind === "olt" || i.kind === "pon");
  const quiet = state === "healthy";

  return (
    // Dark, chrome-free, and it must fill the TV: the app shell is not rendered
    // for this route, so the page owns the whole viewport.
    <div className="flex h-dvh flex-col gap-[0.6rem] overflow-hidden bg-slate-950 p-[0.8rem] text-slate-100">
      <Header fresh={fresh} now={now} alarm={alarm} />
      <MonitoringBanner compact />
      {/* A stale board never states a network condition: the data behind it is not current. */}
      <Posture data={data} inc={inc} state={fresh.stale ? null : state} />
      {fresh.stale && <StaleBanner fresh={fresh} now={now} dataUpdatedAt={dataUpdatedAt} />}
      {!fresh.stale && state === "major" && <MajorBanner major={major} now={now} />}
      {/* Stale data is dimmed and desaturated so old numbers never read as live. */}
      <main className={cn("grid min-h-0 flex-1 gap-[0.6rem]", fresh.stale && "opacity-45 grayscale-[70%]",
        quiet ? "grid-cols-[minmax(0,1.15fr)_minmax(0,1fr)_minmax(0,1fr)]" : "grid-cols-[minmax(0,1.4fr)_minmax(0,1fr)_minmax(0,1fr)]")}>
        {quiet ? (
          // Healthy: nothing to report, so the board shows the infrastructure itself.
          <>
            <Fleet data={data} now={now} />
            <PonHealth data={data} now={now} />
            <div className="grid min-h-0 grid-rows-[minmax(0,1.25fr)_minmax(0,1fr)] gap-[0.6rem]">
              <Optical data={data} />
              <Uplinks data={data} />
            </div>
          </>
        ) : (
          // Something is wrong: service impact takes the widest column, first.
          <>
            <ServiceImpact inc={inc} now={now} />
            <div className="grid min-h-0 grid-rows-[minmax(0,1fr)_minmax(0,1fr)] gap-[0.6rem]">
              <Fleet data={data} now={now} />
              <PonHealth data={data} now={now} />
            </div>
            <div className="grid min-h-0 grid-rows-[minmax(0,1fr)_minmax(0,1.2fr)] gap-[0.6rem]">
              <Warnings data={data} now={now} />
              <Optical data={data} />
            </div>
          </>
        )}
      </main>
    </div>
  );
}

// --- header: identity, telemetry freshness, wall clock ---------------------

const ageText = (s: number | null, now: number) => (s === null ? "—" : dur(new Date(now - s * 1000).toISOString(), now));

// --- alarm: sound on new outages, until acknowledged ---------------------------

const SOUND_KEY = "nocops.wb.sound";

type Alarm = ReturnType<typeof useAlarm>;

/**
 * Sounds when an incident appears that was not open on the previous poll.
 * The first fresh poll is the baseline (outages already open when the board
 * loads do not ring), stale data never rings, and an unacknowledged alarm
 * repeats while its incidents stay open.
 */
function useAlarm(inc: Incident[] | null, stale: boolean, stamp: number) {
  const [enabled, setEnabled] = useState(() => localStorage.getItem(SOUND_KEY) !== "off");
  const [armed, setArmed] = useState(audioArmed);
  const [pending, setPending] = useState<Incident[]>([]);
  const sounded = useRef<Map<string, number> | null>(null);

  // Browsers unlock audio on a user gesture: the first click or key on the board arms it.
  useEffect(() => {
    if (armed) return;
    const arm = () => { void armAudio().then(setArmed); };
    window.addEventListener("pointerdown", arm);
    window.addEventListener("keydown", arm);
    return () => { window.removeEventListener("pointerdown", arm); window.removeEventListener("keydown", arm); };
  }, [armed]);

  useEffect(() => {
    if (!inc || stale) return;
    const now = Date.now();
    if (!sounded.current) {
      sounded.current = new Map();
      alarmable(inc, sounded.current, now);
      return;
    }
    const fresh = alarmable(inc, sounded.current, now);
    const open = new Set(inc.map((i) => i.key));
    setPending((p) => [...p.filter((x) => open.has(x.key)), ...fresh]);
    if (fresh.length && enabled) playAlarm(alarmLevel(fresh));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [stamp]);

  useEffect(() => {
    if (!pending.length || !enabled) return;
    const id = setInterval(() => playAlarm(alarmLevel(pending)), ALARM_REPEAT_MS);
    return () => clearInterval(id);
  }, [pending, enabled]);

  const toggle = async () => {
    const next = !enabled;
    setEnabled(next);
    localStorage.setItem(SOUND_KEY, next ? "on" : "off");
    if (next) {
      const ok = await armAudio();
      setArmed(ok);
      if (ok) playAlarm("minor"); // audible confirmation that sound works
    }
  };
  return { enabled, armed, pending, toggle, ack: () => setPending([]) };
}

function AlarmControls({ alarm }: { alarm: Alarm }) {
  const { t } = useI18n();
  const major = alarm.pending.some((i) => i.kind === "olt" || i.kind === "pon");
  return (
    <div className="flex items-center gap-[0.6rem]">
      {alarm.pending.length > 0 && (
        <>
          <span className={cn("flex animate-pulse items-center gap-[0.45rem] px-[0.7rem] py-[0.15rem] text-[1.1rem] font-semibold tracking-[0.12em]",
            major ? "bg-signal-crit text-white" : "bg-signal-warn text-slate-950")}>
            <FiBell /> {t("wb.alarm.new", { n: alarm.pending.length })}
          </span>
          <button type="button" onClick={alarm.ack}
            className="border border-slate-600 px-[0.7rem] py-[0.15rem] text-[1rem] font-semibold tracking-[0.1em] text-slate-100 hover:bg-white/10">
            {t("wb.alarm.ack")}
          </button>
        </>
      )}
      {alarm.enabled && !alarm.armed && (
        <span className="border border-signal-warn/60 px-[0.6rem] py-[0.1rem] text-[0.95rem] text-signal-warn">{t("wb.sound.arm")}</span>
      )}
      <button type="button" onClick={() => void alarm.toggle()} aria-pressed={alarm.enabled} title={t(alarm.enabled ? "wb.sound.on" : "wb.sound.off")}
        className={cn("grid size-[2.2rem] place-items-center text-[1.3rem] hover:bg-white/10", alarm.enabled ? "text-slate-300" : "text-slate-500")}>
        {alarm.enabled ? <FiVolume2 /> : <FiVolumeX />}
      </button>
    </div>
  );
}

function Header({ fresh, now, alarm }: { fresh: Freshness; now: number; alarm: Alarm }) {
  const { t } = useI18n();
  return (
    <header className="flex shrink-0 items-center justify-between gap-[1rem] px-[0.2rem]">
      <h1 className="min-w-0 truncate text-[1.6rem] font-semibold tracking-[0.35em] text-slate-200">JALABYTE NOC</h1>
      <div className="flex shrink-0 items-center gap-[1.6rem]">
        <AlarmControls alarm={alarm} />
        {fresh.stale ? (
          <span className="flex items-center gap-[0.5rem] bg-signal-warn px-[0.7rem] py-[0.15rem] text-[1.15rem] font-semibold tracking-[0.12em] text-slate-950">
            <FiAlertTriangle /> {t("wb.stale")}
          </span>
        ) : (
          // "Live" means the OLT poller ran recently, not that the page is open.
          <span className="flex items-center gap-[0.55rem] text-[1.05rem]">
            <span className="beacon size-[0.6rem] rounded-full bg-signal-good text-signal-good" />
            <span className="font-semibold tracking-[0.2em] text-signal-good">{t("wb.live")}</span>
            <span className="font-mono text-slate-400">· {t("wb.polled", { age: ageText(fresh.pollAgeS, now) })}</span>
          </span>
        )}
        <span className="font-mono text-[1.6rem] tabular-nums text-slate-300">{clockFmt.format(now)} WIB</span>
      </div>
    </header>
  );
}

// --- posture: "is the network healthy?" in one glance ------------------------

const STATE_TONE: Record<NetState, Tier> = { healthy: "healthy", degraded: "degraded", major: "down" };

function Posture({ data, inc, state }: { data: Overview; inc: Incident[]; state: NetState | null }) {
  const { t, locale } = useI18n();
  const tot = data.totals;
  const down = tot.onts_offline + tot.onts_missing;
  const tone = state ? STATE_TONE[state] : "unknown";
  const Icon = state === "major" ? FiAlertOctagon : state ? TONE[tone].icon : FiClock;
  // Weight follows consequence: network state and DOWN lead, warnings trail.
  return (
    <div className="grid shrink-0 grid-cols-[minmax(0,2.1fr)_minmax(0,1fr)_minmax(0,1.35fr)_minmax(0,1fr)_minmax(0,1fr)_minmax(0,0.8fr)] border border-slate-800/90 bg-slate-900/35">
      <div className={cn("flex items-center gap-[0.8rem] border-r border-slate-800/90 px-[1.2rem] py-[0.9rem]",
        state === "major" && "bg-signal-crit/15", state === "degraded" && "bg-signal-warn/[0.07]")}>
        <Icon className={cn("size-[2.6rem] shrink-0", TONE[tone].text)} />
        <div>
          <div className="text-[0.8rem] uppercase tracking-[0.25em] text-slate-400">{t("wb.network")}</div>
          <div className={cn("text-[2.3rem] font-semibold leading-tight tracking-[0.06em]", TONE[tone].text)}>
            {t(state ? (`wb.state.${state}` as Key) : "wb.state.unknown")}
          </div>
        </div>
      </div>
      <div className={cn("contents", !state && "[&>*]:opacity-45 [&>*]:grayscale-[70%]")}>
      <Figure label={t("wb.olt")} value={`${fmtNum(locale, tot.olts_reachable)}/${fmtNum(locale, tot.olts)}`} sub={t("wb.online")}
        tone={tot.olts_reachable < tot.olts ? "down" : "quiet"} />
      <Figure label={t("wb.ont")} value={`${fmtNum(locale, tot.onts_online)}/${fmtNum(locale, tot.onts)}`} sub={t("wb.online")}
        tone={down ? "degraded" : "quiet"} />
      <Figure label={t("wb.down")} value={fmtNum(locale, down)} sub={t("wb.ontsOffline")} tone={down ? "down" : "quiet"} big />
      <Figure label={t("wb.incidents")} value={fmtNum(locale, inc.length)} sub={t("wb.correlated")} tone={inc.length ? "down" : "quiet"} />
      <Figure label={t("wb.warnings")} value={fmtNum(locale, tot.alerts_warn)} sub={t("wb.alertsOpen", { n: fmtNum(locale, tot.alerts_open) })}
        tone={tot.alerts_warn ? "warn" : "quiet"} small />
      </div>
    </div>
  );
}

function Figure({ label, value, sub, tone, big, small }: {
  label: string; value: string; sub: string; tone: Tier | "quiet"; big?: boolean; small?: boolean;
}) {
  const color = tone === "quiet" ? "text-slate-200" : TONE[tone].text;
  return (
    <div className="@container flex min-w-0 flex-col justify-center border-r border-slate-800/90 px-[1.1rem] py-[0.7rem] last:border-r-0">
      <div className="truncate text-[0.8rem] uppercase tracking-[0.25em] text-slate-400">{label}</div>
      {/* Big, but never wider than its column: "3,316/3,500" shrinks, "2" does not. */}
      <div className={cn("font-mono font-semibold leading-none whitespace-nowrap tabular-nums", color)}
        style={{ fontSize: `min(${big ? 3.6 : small ? 2.2 : 3}rem, ${(100 / (value.length * 0.62)).toFixed(1)}cqw)` }}>{value}</div>
      <div className="mt-[0.3rem] truncate text-[0.8rem] uppercase tracking-[0.18em] text-slate-500">{sub}</div>
    </div>
  );
}

// --- banners: a major incident dominates; stale data is unmissable -----------

function MajorBanner({ major, now }: { major: Incident[]; now: number }) {
  const { t, locale } = useI18n();
  const top = major[0];
  const name = top.kind === "olt" ? top.olt.name : top.kind === "pon" ? `${top.pon.olt_name} · PON ${top.pon.pon_path}` : "";
  return (
    <div className="flex shrink-0 items-center gap-[1.4rem] border-2 border-signal-crit bg-signal-crit/20 px-[1.2rem] py-[0.8rem]">
      <FiAlertOctagon className="size-[3rem] shrink-0 text-signal-crit" />
      <div className="min-w-0 flex-1">
        <div className="text-[0.95rem] font-semibold uppercase tracking-[0.3em] text-signal-crit">
          {t("wb.majorIncident")}{major.length > 1 && ` · +${major.length - 1} ${t("wb.more")}`}
          {top.site && <span className="ml-[0.8rem] text-red-200">{top.site}</span>}
        </div>
        <div className="truncate text-[2.4rem] font-semibold leading-tight text-white">
          {name} {t(top.kind === "olt" ? "wb.oltDown" : "wb.ponDown")}
        </div>
      </div>
      <div className="text-right">
        <div className="font-mono text-[2.4rem] font-semibold leading-none text-white tabular-nums">{fmtNum(locale, top.affected)}</div>
        <div className="text-[0.85rem] uppercase tracking-[0.18em] text-red-200">{t("wb.ontsPotentially")}</div>
      </div>
      <div className="border-l border-signal-crit/50 pl-[1.4rem] text-right">
        <div className="font-mono text-[2.4rem] font-semibold leading-none text-white tabular-nums">{dur(top.since, now)}</div>
        <div className="text-[0.85rem] uppercase tracking-[0.18em] text-red-200">{t("wb.downFor")}</div>
      </div>
    </div>
  );
}

function StaleBanner({ fresh, now, dataUpdatedAt }: { fresh: Freshness; now: number; dataUpdatedAt: number }) {
  const { t } = useI18n();
  const msg = fresh.reason === "api"
    ? t("wb.staleApi", { age: dur(new Date(dataUpdatedAt).toISOString(), now) })
    : fresh.reason === "never"
      ? t("wb.staleNever")
      : t("wb.stalePoll", { age: ageText(fresh.pollAgeS, now) });
  return (
    <div className="flex shrink-0 items-center gap-[1.2rem] border-2 border-signal-warn bg-signal-warn/15 px-[1.2rem] py-[0.8rem]">
      <FiClock className="size-[2.6rem] shrink-0 text-signal-warn" />
      <div>
        <div className="text-[2rem] font-semibold tracking-[0.08em] text-signal-warn">{t("wb.staleTitle")}</div>
        <div className="text-[1.15rem] text-amber-100">{msg} {t("wb.staleHint")}</div>
      </div>
    </div>
  );
}

// --- SERVICE IMPACT: correlated, prioritised, sized by consequence -----------

function ServiceImpact({ inc, now }: { inc: Incident[]; now: number }) {
  const { t, locale } = useI18n();
  const onts = inc.reduce((n, i) => n + i.affected, 0);
  return (
    <Panel title={t("wb.serviceImpact")} aside={t("wb.ontsAffected", { n: fmtNum(locale, onts) })}>
      <FitList
        items={inc}
        keyOf={(i) => i.key}
        row={(i) => <IncidentRow i={i} now={now} />}
        rest={(hidden) => (
          <Rest healthy={false} label={t("wb.moreIncidents", {
            n: hidden.length, onts: fmtNum(locale, hidden.reduce((n, i) => n + i.affected, 0)),
          })} />
        )}
        empty={<Calm text={t("wb.noImpact")} />}
      />
    </Panel>
  );
}

function IncidentRow({ i, now }: { i: Incident; now: number }) {
  const { t, locale } = useI18n();
  const infra = i.kind === "olt" || i.kind === "pon";
  const title = t(`wb.kind.${i.kind}` as Key, { n: fmtNum(locale, i.affected) });
  let name: string, where: string, note: string;
  if (i.kind === "olt") {
    name = i.olt.name;
    where = i.site ?? "";
    note = t("wb.potentiallyN", { n: fmtNum(locale, i.affected) });
  } else if (i.kind === "pon" || i.kind === "multi") {
    name = `${i.pon.olt_name} · PON ${i.pon.pon_path}`;
    where = i.site ?? "";
    note = t("wb.onlineOf", { on: fmtNum(locale, i.pon.onts_online), all: fmtNum(locale, i.pon.onts_total) });
  } else {
    name = i.ont.description || i.ont.serial;
    where = `${i.ont.olt_name} · PON ${i.ont.pon_path} · ONT ${i.ont.ont_id}`;
    // Dying gasp is evidence of a power loss, not proof: say what was measured.
    note = i.powerLoss ? t("wb.powerLoss") : i.ont.last_down_cause ? t("wb.lastCause", { cause: i.ont.last_down_cause }) : "";
  }
  return (
    <div className={cn("flex items-start gap-[0.8rem] border-b border-l-[0.3rem] border-b-slate-800/70 px-[1rem] py-[0.55rem]",
      infra ? "border-l-signal-crit bg-signal-crit/[0.08]" : "border-l-signal-crit/50")}>
      <Marker tier="down" className="mt-[0.35rem]" />
      <div className="min-w-0 flex-1">
        <div className={cn("text-[0.85rem] font-semibold uppercase tracking-[0.2em]", infra ? "text-signal-crit" : "text-red-300")}>{title}</div>
        <div className={cn("truncate font-medium leading-tight text-slate-50", infra ? "text-[1.7rem]" : "text-[1.35rem]")}>{name}</div>
        <div className="truncate font-mono text-[0.95rem] text-slate-400">{where}</div>
        {note && (
          <div className={cn("truncate text-[0.95rem]", i.kind === "ont" && i.powerLoss ? "text-amber-200" : "text-slate-300")}>{note}</div>
        )}
      </div>
      <div className="text-right">
        <div className={cn("font-mono tabular-nums text-slate-100", infra ? "text-[1.6rem]" : "text-[1.25rem]")}>{dur(i.since, now)}</div>
        <div className="text-[0.75rem] uppercase tracking-[0.18em] text-slate-500">{t("wb.downFor")}</div>
      </div>
    </div>
  );
}

// --- OLT FLEET, grouped by POP/site once sites exist -------------------------

type FleetRow = { kind: "site"; site: Site } | { kind: "olt"; olt: Overview["olts"][number]; indent: boolean };

const upFor = (s: number | null) => {
  if (s === null) return null;
  const d = Math.floor(s / 86400), h = Math.floor(s / 3600);
  return d > 0 ? `${d}d` : h > 0 ? `${h}h` : `${Math.floor(s / 60)}m`;
};

function Fleet({ data, now }: { data: Overview; now: number }) {
  const { t, locale } = useI18n();
  const byS = sites(data.olts, data.pons);
  // With sites: one row per site; an abnormal site also lists its abnormal OLTs.
  const rows: FleetRow[] = byS
    ? byS.flatMap((s) => [
      { kind: "site" as const, site: s },
      ...s.olts.filter((o) => oltTier(o, data.pons) !== "healthy").map((o) => ({ kind: "olt" as const, olt: o, indent: true })),
    ])
    : rankOLTs(data.olts, data.pons).map((o) => ({ kind: "olt" as const, olt: o, indent: false }));
  const tierOf = (r: FleetRow) => (r.kind === "site" ? r.site.tier : oltTier(r.olt, data.pons));
  const unit = t(byS ? "wb.sitesWord" : "wb.oltsWord");
  return (
    <Panel title={t(byS ? "wb.sites" : "wb.fleet")}
      aside={t("wb.oltsOnline", { up: fmtNum(locale, data.totals.olts_reachable), all: fmtNum(locale, data.olts.length) })}>
      <FitList
        items={rows}
        keyOf={(r) => (r.kind === "site" ? `s${r.site.name}` : `o${r.olt.id}`)}
        row={(r) => (r.kind === "site" ? <SiteRow s={r.site} />
          : <OltRow o={r.olt} tier={oltTier(r.olt, data.pons)} indent={r.indent} now={now} pollS={data.olt_poll_interval_s} />)}
        rest={(hidden) => {
          // Count what the unit names: with sites, hidden OLT sub-rows are not sites.
          const counted = byS ? hidden.filter((r) => r.kind === "site") : hidden;
          const bad = counted.filter((r) => tierOf(r) !== "healthy").length;
          const n = counted.length || hidden.length;
          return <Rest healthy={bad === 0} label={bad === 0
            ? t("wb.otherHealthy", { n, unit })
            : t("wb.moreAbnormal", { n, unit, bad })} />;
        }}
      />
    </Panel>
  );
}

function SiteRow({ s }: { s: Site }) {
  const { t, locale } = useI18n();
  const off = s.ontsTotal - s.ontsOnline;
  return (
    <div className="flex items-center gap-[0.8rem] border-b border-slate-800/70 px-[1rem] py-[0.45rem]">
      <Marker tier={s.tier} />
      <div className="min-w-0 flex-1">
        <div className={cn("truncate text-[1.35rem] font-medium leading-tight", s.tier === "healthy" ? "text-slate-200" : "text-slate-50")}>
          {s.name ?? t("wb.noSite")}
        </div>
        <div className="truncate font-mono text-[0.95rem] text-slate-400">
          {s.oltsUp}/{s.olts.length} OLT · {fmtNum(locale, s.ontsOnline)}/{fmtNum(locale, s.ontsTotal)} ONT
          {off > 0 && <span className={s.tier === "healthy" ? "" : TONE[s.tier].text}> · {t("wb.offlineN", { n: fmtNum(locale, off) })}</span>}
        </div>
      </div>
    </div>
  );
}

function OltRow({ o, tier, indent, now, pollS }: { o: Overview["olts"][number]; tier: Tier; indent: boolean; now: number; pollS: number }) {
  const { t, locale } = useI18n();
  const polled = o.last_polled_at ? (now - Date.parse(o.last_polled_at)) / 1000 : null;
  const why = tier === "down" ? t("wb.oltDown") : o.status === "degraded" ? t("wb.oltPartial")
    : tier === "degraded" ? t("wb.ponTrouble") : t("wb.hot");
  return (
    <div className={cn("flex items-center gap-[0.8rem] border-b border-slate-800/70 py-[0.5rem] pr-[1rem]", indent ? "pl-[2.4rem]" : "pl-[1rem]")}>
      <Marker tier={tier} />
      <div className="min-w-0 flex-1">
        <div className={cn("truncate font-medium", indent ? "text-[1.15rem]" : "text-[1.35rem]", tier === "healthy" ? "text-slate-200" : "text-slate-50")}>{o.name}</div>
        {tier !== "healthy" && <div className={cn("truncate text-[0.85rem] uppercase tracking-[0.16em]", TONE[tier].text)}>{why}</div>}
      </div>
      <span className="font-mono text-[1.1rem] tabular-nums text-slate-200">{fmtNum(locale, o.onts_online)}/{fmtNum(locale, o.onts_total)}</span>
      {o.onts_offline > 0 && tier !== "down" && <span className="font-mono text-[0.95rem] text-slate-400">{t("wb.offlineN", { n: fmtNum(locale, o.onts_offline) })}</span>}
      {/* A dead OLT's temperature and uptime are its last readings, not current ones. */}
      {o.temp_c !== null && tier !== "down" && (
        // Coloured on the box's own scale: over 60 C is hot for a GPON OLT
        // SFP, over 70 C is where vendors start derating.
        <span className={cn("w-[4.2rem] text-right font-mono text-[1rem]",
          o.temp_c >= HOT_CRIT_C ? "text-signal-crit" : o.temp_c >= HOT_C ? "text-signal-warn" : "text-slate-400")} title={o.temp_descr}>
          {o.temp_c.toFixed(1)}°C
        </span>
      )}
      {upFor(o.uptime_s) && tier !== "down" && <span className="w-[4.5rem] text-right font-mono text-[0.9rem] text-slate-500">{t("wb.up")} {upFor(o.uptime_s)}</span>}
      {/* Only when it matters: an OLT the poller has not reached for two cycles. */}
      {polled !== null && polled > pollS * 2 && (
        <span className="font-mono text-[0.9rem] text-signal-warn">{t("wb.polledAgo", { age: dur(o.last_polled_at, now) })}</span>
      )}
    </div>
  );
}

// --- PON HEALTH: abnormal PONs float, healthy collapse -----------------------

function PonHealth({ data, now }: { data: Overview; now: number }) {
  const { t, locale } = useI18n();
  const ranked = rankPONs(data.pons);
  const full = data.pons.filter((p) => ponTier(p) === "healthy").length;
  const multiOLT = new Set(data.pons.map((p) => p.olt_id)).size > 1;
  const unit = t("wb.ponsWord");
  return (
    <Panel title={t("wb.ponHealth")} aside={t("wb.ponsFull", { up: fmtNum(locale, full), all: fmtNum(locale, data.pons.length) })}>
      <FitList
        items={ranked}
        keyOf={(p) => String(p.id)}
        row={(p) => {
          const tier = ponTier(p);
          const off = p.onts_total - p.onts_online;
          return (
            <div className="flex items-center gap-[0.8rem] border-b border-slate-800/70 px-[1rem] py-[0.42rem]">
              <Marker tier={tier} />
              {/* Path first: when names are long, the OLT name truncates, never the PON. */}
              <span className="min-w-0 flex-1 truncate text-[1.15rem]">
                <span className={cn("font-mono", tier === "healthy" ? "text-slate-300" : "text-slate-50")}>{p.pon_path}</span>
                {multiOLT && <span className={tier === "healthy" ? "text-slate-500" : "text-slate-300"}> · {p.olt_name}</span>}
              </span>
              {p.weak > 0 && <span className="font-mono text-[0.95rem] text-signal-warn">{t("wb.weakN", { n: p.weak })}</span>}
              {tier === "down" && p.last_down_at && <span className="font-mono text-[0.95rem] text-signal-crit">{t("wb.downN", { age: dur(p.last_down_at, now) })}</span>}
              {off > 0 && tier !== "down" && <span className={cn("font-mono text-[0.95rem]", tier === "degraded" ? "text-signal-warn" : "text-slate-400")}>{t("wb.offlineN", { n: off })}</span>}
              <span className={cn("w-[5.5rem] text-right font-mono text-[1.1rem] tabular-nums", tier === "down" ? "text-signal-crit" : "text-slate-200")}>
                {fmtNum(locale, p.onts_online)}/{fmtNum(locale, p.onts_total)}
              </span>
            </div>
          );
        }}
        rest={(hidden) => {
          const bad = hidden.filter((p) => ponTier(p) !== "healthy").length;
          return <Rest healthy={bad === 0} label={bad === 0
            ? t("wb.otherHealthy", { n: hidden.length, unit })
            : t("wb.moreAbnormal", { n: hidden.length, unit, bad })} />;
        }}
        empty={<Calm text={t("wb.noPons")} />}
      />
    </Panel>
  );
}

// --- NETWORK WARNINGS: degradation that is not service impact ----------------

type Warn = { key: string; title: string; target: string; value: string; since: string | null };

function Warnings({ data, now }: { data: Overview; now: number }) {
  const { t } = useI18n();
  const rows: Warn[] = [
    ...data.olts.filter((o) => o.status === "degraded").map((o) => ({
      key: `deg${o.id}`, title: t("wb.oltPartial"), target: o.name, value: "", since: null,
    })),
    ...data.olts.filter((o) => o.temp_c !== null && o.temp_c >= HOT_C && o.status !== "unreachable").map((o) => ({
      key: `hot${o.id}`, title: t("wb.hot"), target: o.name, value: `${o.temp_c!.toFixed(1)}°C`, since: null,
    })),
    // The OLT poller's own failures are OLT outages, already under SERVICE IMPACT.
    ...data.collectors.filter((c) => c.status === "error" && c.collector !== "olt").map((c) => ({
      key: `col${c.collector}`, title: t("wb.collectorError"), target: c.collector, value: "", since: c.finished_at,
    })),
    ...warningAlerts(data.alerts_open).map((a) => ({
      key: `a${a.id}`, title: incidentTitle(t, a), target: incidentTarget(a, data.olts), since: a.opened_at,
      value: a.value === null ? "" : String(Math.round(a.value * 10) / 10),
    })),
  ];
  // alerts_open is capped: the uncapped counts say how many were not listed.
  const { optical, other } = alertBreakdown(data);
  const listedAlerts = rows.filter((r) => r.key.startsWith("a")).length;
  const unlisted = Math.max(0, other - listedAlerts);
  return (
    <Panel title={t("wb.warnings")} aside={rows.length + unlisted || undefined}>
      {unlisted > 0 && (
        <div className="shrink-0 border-b border-slate-800/70 px-[1rem] py-[0.4rem] text-[0.95rem] text-signal-warn">
          {t("wb.unlisted", { n: unlisted })}
        </div>
      )}
      <FitList
        items={rows}
        keyOf={(w) => w.key}
        row={(w) => (
          <div className="flex items-center gap-[0.8rem] border-b border-slate-800/70 px-[1rem] py-[0.42rem]">
            <Marker tier="warn" />
            <div className="min-w-0 flex-1">
              <div className="truncate text-[1.1rem] text-slate-100">{w.target}</div>
              <div className="truncate text-[0.8rem] uppercase tracking-[0.16em] text-signal-warn">{w.title}</div>
            </div>
            {w.value && <span className="font-mono text-[1.05rem] text-signal-warn">{w.value}</span>}
            {w.since && <span className="font-mono text-[0.9rem] text-slate-500">{dur(w.since, now)}</span>}
          </div>
        )}
        rest={(hidden) => <Rest healthy={false} label={t("wb.moreWarnings", { n: hidden.length })} />}
        empty={
          <>
            {unlisted === 0 && <Calm text={t("wb.noWarnings")} />}
            {optical > 0 && <p className="px-[1rem] py-[0.5rem] text-[1rem] text-slate-400">{t("wb.opticalElsewhere", { n: optical })}</p>}
          </>
        }
      />
    </Panel>
  );
}

// --- OPTICAL HEALTH: the count, then the worst few ----------------------------

const km = (m: number | null) => (m === null ? null : m >= 1000 ? `${(m / 1000).toFixed(2)} km` : `${m} m`);

function Optical({ data }: { data: Overview }) {
  const { t, locale } = useI18n();
  const bands = useSignalBands();
  const weak = data.totals.onts_weak;
  return (
    <Panel title={t("wb.optical")}>
      <div className="flex shrink-0 items-baseline gap-[0.6rem] border-b border-slate-800/70 px-[1rem] py-[0.5rem]">
        <span className={cn("font-mono text-[2.2rem] font-semibold leading-none", weak ? "text-signal-warn" : "text-slate-300")}>{fmtNum(locale, weak)}</span>
        <span className="text-[0.95rem] text-slate-400">{t("wb.belowWarn", { dbm: data.optical_warn_dbm })}</span>
      </div>
      {/* The worst five, not a ranking: this is a maintenance cue, not an incident list. */}
      <FitList
        items={data.worst_signal.slice(0, 5)}
        keyOf={(s) => String(s.id)}
        row={(s) => (
          <div className="flex items-center gap-[0.8rem] border-b border-slate-800/70 px-[1rem] py-[0.4rem]">
            <div className="min-w-0 flex-1">
              <div className="truncate text-[1.1rem] text-slate-100">{s.description || s.serial}</div>
              <div className="truncate font-mono text-[0.85rem] text-slate-500">
                {s.olt_name} · PON {s.pon_path} · ONT {s.ont_id}{km(s.distance_m) && ` · ${km(s.distance_m)}`}
              </div>
            </div>
            <span className={cn("font-mono text-[1.25rem] tabular-nums", signalColor[classifyRx(s.rx_power_dbm, bands)])}>{fmtDbm(s.rx_power_dbm)}</span>
          </div>
        )}
        rest={() => null}
        empty={<Calm text={t("wb.noOptical")} />}
      />
    </Panel>
  );
}

// --- UPLINKS: on a quiet network, "is traffic flowing, is anything full?" -----

function Uplinks({ data }: { data: Overview }) {
  const { t } = useI18n();
  return (
    <Panel title={t("wb.uplinks")}>
      <FitList
        items={data.busiest_links}
        keyOf={(l) => `${l.origin}${l.id}`}
        row={(l) => {
          const u = l.util_pct;
          const tone = l.state === "down" ? "text-signal-crit" : u === null ? "text-slate-400"
            : u > 80 ? "text-signal-crit" : u >= 50 ? "text-signal-warn" : "text-slate-200";
          return (
            <div className="flex items-center gap-[0.8rem] border-b border-slate-800/70 px-[1rem] py-[0.4rem]">
              <span className="min-w-0 flex-1 truncate text-[1.05rem] text-slate-200">{l.label ?? `${l.source_name} → ${l.target_name}`}</span>
              <span className="font-mono text-[0.9rem] text-slate-400">↓{formatBps(l.rx_bps)} ↑{formatBps(l.tx_bps)}</span>
              <span className={cn("w-[3.5rem] text-right font-mono text-[1.1rem]", tone)}>{u === null ? "—" : `${Math.round(u)}%`}</span>
            </div>
          );
        }}
        rest={() => null}
        empty={<Calm text={t("wb.noUplinks")} />}
      />
    </Panel>
  );
}
